/**
 * Générateur — Calculer l'aire d'un carré ou d'un rectangle
 * Attendu ID : 646 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, texte, relier, texte_trou_saisie
 * Labels de référence :
 *   - Calculer l'aire d'un carré ou rectangle (figure SVG)
 *   - Calculer l'aire et le périmètre d'un carré ou rectangle
 * Variantes estimées : 2 000+
 */

import {
    randomChoice, randInt,
    makeQcm, makeSaisie, makeTexteTrou, makeRelier,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 646;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const PRENOMS = [
    { nom: 'Adam', fem: false }, { nom: 'Amélie', fem: true }, { nom: 'Chloé', fem: true },
    { nom: 'Hugo', fem: false }, { nom: 'Inès', fem: true }, { nom: 'Jade', fem: true },
    { nom: 'Karim', fem: false }, { nom: 'Léo', fem: false }, { nom: 'Lucas', fem: false },
    { nom: 'Manon', fem: true }, { nom: 'Sofia', fem: true }, { nom: 'Thomas', fem: false },
    { nom: 'Yanis', fem: false },
];

const UNITES = ['cm', 'dm', 'm'];

// ─── Helpers ───

/** Formate un nombre pour l'affichage français */
const fmt = (n) => String(n).replace('.', ',');

/** Arrondit proprement */
const clean = (n) => +(Math.round(n * 1e8) / 1e8);

/** Génère un côté entier */
const randCote = () => randInt(2, 15);

/** Génère L et l pour un rectangle (L > l) */
const randRect = (decimal = false) => {
    if (decimal) {
        const L = clean(randInt(30, 125) / 10);
        const lMax = Math.floor((L - 0.5) * 10);
        const lMin = 15;
        if (lMax <= lMin) return { L, l: clean(lMin / 10) };
        const l = clean(randInt(lMin, lMax) / 10);
        return { L, l };
    }
    const L = randInt(3, 15);
    const l = randInt(2, L - 1);
    return { L, l };
};

/** Génère un SVG de rectangle */
const svgRect = (L, l, Lval, lval, unite) => {
    // Marge gauche élargie + ancrage end (revue 2026-07-31) : « 12,5 dm »
    // débordait sur le bord gauche du rectangle dans 11 % des tirages.
    const W = 200, H = Math.round(W * Math.min(l / L, 0.8));
    const ox = 70, oy = 30;
    const sq = 12;
    return `<svg viewBox="0 0 ${ox * 2 + W} ${oy * 2 + H}" xmlns="http://www.w3.org/2000/svg" style="max-width:${ox * 2 + W}px;display:block;margin:8px auto">` +
        `<rect x="${ox}" y="${oy}" width="${W}" height="${H}" fill="rgba(219,234,254,0.15)" stroke="currentColor" stroke-width="2"/>` +
        `<polyline points="${ox + sq},${oy} ${ox + sq},${oy + sq} ${ox},${oy + sq}" fill="none" stroke="#94a3b8" stroke-width="1"/>` +
        `<text x="${ox + W / 2}" y="${oy - 10}" text-anchor="middle" font-size="14" fill="currentColor" font-family="system-ui,sans-serif">${fmt(Lval)} ${unite}</text>` +
        `<text x="${ox - 6}" y="${oy + H / 2 + 5}" text-anchor="end" font-size="14" fill="currentColor" font-family="system-ui,sans-serif">${fmt(lval)} ${unite}</text>` +
        `</svg>`;
};

/** Génère un SVG de carré */
const svgCarre = (c, unite) => {
    const S = 150, ox = 40, oy = 30;
    const sq = 12;
    return `<svg viewBox="0 0 ${ox * 2 + S} ${oy * 2 + S}" xmlns="http://www.w3.org/2000/svg" style="max-width:${ox * 2 + S}px;display:block;margin:8px auto">` +
        `<rect x="${ox}" y="${oy}" width="${S}" height="${S}" fill="rgba(219,234,254,0.15)" stroke="currentColor" stroke-width="2"/>` +
        `<polyline points="${ox + sq},${oy} ${ox + sq},${oy + sq} ${ox},${oy + sq}" fill="none" stroke="#94a3b8" stroke-width="1"/>` +
        `<text x="${ox + S / 2}" y="${oy - 10}" text-anchor="middle" font-size="14" fill="currentColor" font-family="system-ui,sans-serif">${fmt(c)} ${unite}</text>` +
        `</svg>`;
};

// ─── Labels ───
const L_FIGURE = 'Calculer l\'aire d\'un carré ou rectangle (figure SVG)';
const L_PERIM = 'Calculer l\'aire et le périmètre d\'un carré ou rectangle';

// ═══════════════════════════════════════════
// EXERCICE 1 — Calculer l'aire avec SVG
// ═══════════════════════════════════════════

const genFigureQcm = () => {
    const unite = randomChoice(UNITES);
    const u2 = unite + '²';
    const isCarré = Math.random() < 0.4;

    if (isCarré) {
        let c;
        do { c = randCote(); } while (c === 2 || c === 4);
        const aire = c * c;
        const svg = svgCarre(c, unite);
        const dSet = new Set([4 * c, 2 * c, c * c * 2]);
        dSet.delete(aire);
        const dArr = [...dSet];
        while (dArr.length < 3) dArr.push(aire + randInt(1, 5) * c);
        const choices = [aire, ...dArr.slice(0, 3)].map(v => `${fmt(v)} ${u2}`);
        const texte = `<p>Quelle est l'aire de ce carré ?</p>${svg}`;
        const payload = makeQcm(choices, choices[0], texte, opts(L_FIGURE));
        // Calculatrice AUTORISÉE (revue 2026-07-31) : 30 % des rectangles ont des
    // décimales (12,5 × 3,4) — la compétence est la formule, pas la technique.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_aire_figure';
        return payload;
    }

    const useDec = Math.random() < 0.3;
    const { L, l } = randRect(useDec);
    const aire = clean(L * l);
    const perim = clean(2 * (L + l));
    const svg = svgRect(L, l, L, l, unite);
    const dSet = new Set([perim, clean(L + l), clean(aire * 2)]);
    dSet.delete(aire);
    const dArr = [...dSet];
    while (dArr.length < 3) dArr.push(clean(aire + randInt(1, 5)));
    const choices = [aire, ...dArr.slice(0, 3)].map(v => `${fmt(v)} ${u2}`);
    const texte = `<p>Quelle est l'aire de ce rectangle ?</p>${svg}`;
    const payload = makeQcm(choices, choices[0], texte, opts(L_FIGURE));
    // Calculatrice AUTORISÉE (revue 2026-07-31) : 30 % des rectangles ont des
    // décimales (12,5 × 3,4) — la compétence est la formule, pas la technique.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_aire_figure';
    return payload;
};

const genFigureTexte = () => {
    const unite = randomChoice(UNITES);
    const u2 = unite + '²';
    const isCarré = Math.random() < 0.4;

    if (isCarré) {
        const c = randCote();
        const aire = c * c;
        const svg = svgCarre(c, unite);
        const texte = `<p>Calcule l'aire de ce carré, en ${u2}.</p>${svg}`;
        // strict RETIRÉ (revue 2026-07-31, même dossier que 644) : la compétence
        // est le CALCUL de l'aire, pas l'écriture du nombre.
        const payload = makeSaisie(String(aire), texte, { ...opts(L_FIGURE), suffixe: u2 });
        // Calculatrice AUTORISÉE (revue 2026-07-31) : 30 % des rectangles ont des
    // décimales (12,5 × 3,4) — la compétence est la formule, pas la technique.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_aire_figure';
        return payload;
    }

    const useDec = Math.random() < 0.3;
    const { L, l } = randRect(useDec);
    const aire = clean(L * l);
    const svg = svgRect(L, l, L, l, unite);
    const texte = `<p>Calcule l'aire de ce rectangle, en ${u2}.</p>${svg}`;
    const payload = makeSaisie(String(fmt(aire)), texte, { ...opts(L_FIGURE), suffixe: u2 });
    // Calculatrice AUTORISÉE (revue 2026-07-31) : 30 % des rectangles ont des
    // décimales (12,5 × 3,4) — la compétence est la formule, pas la technique.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_aire_figure';
    return payload;
};

// ═══════════════════════════════════════════
// EXERCICE 2 — Aire et périmètre
// ═══════════════════════════════════════════

const genPerimetreQcm = () => {
    const p = randomChoice(PRENOMS);
    const prenom = p.nom;
    const pronom = p.fem ? 'elle' : 'il';
    const unite = randomChoice(UNITES);
    const u2 = unite + '²';
    const isCarré = Math.random() < 0.5;

    if (isCarré) {
        // Évite c=4 (seul cas entier où aire = 4c = périmètre → piège neutralisé)
        let c;
        for (let i = 0; i < 20; i++) {
            c = randCote();
            if (c * c !== 4 * c) break;
        }
        const aire = c * c;
        const perim = 4 * c;
        // ~40 % d'affirmations VRAIES (revue 2026-07-31) : « Oui » n'était
        // JAMAIS la bonne réponse (0/5000) — l'élève apprenait le pattern
        // « toujours Non » et le travail de vérification disparaissait.
        // Garde héritée : c ≠ 4 ⇒ aire ≠ périmètre, jamais deux défendables.
        const affirmationVraie = Math.random() < 0.4;
        const annonce = affirmationVraie ? aire : perim;
        const distCandidats = [2 * c, c, 3 * c, c + 2, c + c + 1]
            .filter(v => v !== aire && v !== perim);
        const distUniques = [...new Set(distCandidats)].slice(0, 2);
        const texte = `<p>${prenom} affirme que l'aire d'un carré de côté ${fmt(c)} ${unite} est de ${fmt(annonce)} ${u2}. A-t-${pronom} raison ?</p>`;
        const choices = affirmationVraie
            ? [
                `Oui, ${fmt(aire)} ${u2} est correct`,
                `Non, l'aire est ${fmt(perim)} ${u2}`,
                `Non, l'aire est ${fmt(distUniques[0])} ${u2}`,
                `Non, l'aire est ${fmt(distUniques[1])} ${u2}`,
            ]
            : [
                `Non, l'aire est ${fmt(aire)} ${u2}`,
                `Oui, ${fmt(perim)} ${u2} est correct`,
                `Non, l'aire est ${fmt(distUniques[0])} ${u2}`,
                `Non, l'aire est ${fmt(distUniques[1])} ${u2}`,
            ];
        const payload = makeQcm(choices, choices[0], texte, opts(L_PERIM));
        payload._meta.generatorKey = 'aire_perimetre';
        return payload;
    }

    // Rectangle : évite L×l = 2(L+l) (ex. 6×3, 4×4)
    let L, l;
    for (let i = 0; i < 20; i++) {
        const r = randRect(false);
        L = r.L; l = r.l;
        if (L * l !== 2 * (L + l)) break;
    }
    const aire = L * l;
    const perim = 2 * (L + l);
    // Même bifurcation « affirmation vraie » que le carré (garde : L×l ≠ 2(L+l)).
    const affirmationVraie = Math.random() < 0.4;
    const annonce = affirmationVraie ? aire : perim;
    const distCandidats = [L + l, aire * 2, L + l + 1, aire + L, aire - l]
        .filter(v => v !== aire && v !== perim && v > 0);
    const distUniques = [...new Set(distCandidats)].slice(0, 2);
    const texte = `<p>${prenom} affirme que l'aire d'un rectangle de longueur ${fmt(L)} ${unite} et de largeur ${fmt(l)} ${unite} est de ${fmt(annonce)} ${u2}. A-t-${pronom} raison ?</p>`;
    const choices = affirmationVraie
        ? [
            `Oui, ${fmt(aire)} ${u2} est correct`,
            `Non, l'aire est ${fmt(perim)} ${u2}`,
            `Non, l'aire est ${fmt(distUniques[0])} ${u2}`,
            `Non, l'aire est ${fmt(distUniques[1])} ${u2}`,
        ]
        : [
            `Non, l'aire est ${fmt(aire)} ${u2}`,
            `Oui, ${fmt(perim)} ${u2} est correct`,
            `Non, l'aire est ${fmt(distUniques[0])} ${u2}`,
            `Non, l'aire est ${fmt(distUniques[1])} ${u2}`,
        ];
    const payload = makeQcm(choices, choices[0], texte, opts(L_PERIM));
    payload._meta.generatorKey = 'aire_perimetre';
    return payload;
};

const genPerimetreTrou = () => {
    const unite = randomChoice(UNITES);
    const u2 = unite + '²';
    const isCarré = Math.random() < 0.4;

    if (isCarré) {
        const c = randCote();
        const aire = c * c;
        const perim = 4 * c;
        // Aération (revue 2026-07-31) : une phrase par ligne.
        const phrase = `Aire = c × c = ${fmt(c)} × ${fmt(c)} = {{${fmt(aire)}}} ${u2}.
`
            + `Périmètre = 4 × c = 4 × ${fmt(c)} = {{${fmt(perim)}}} ${unite}.`;
        const texte = `<p>Calcule l'aire et le périmètre d'un carré de côté ${fmt(c)} ${unite}.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_PERIM), mode: 'saisie', strict: false });
        payload._meta.generatorKey = 'aire_perimetre';
        return payload;
    }

    const { L, l } = randRect(false);
    const aire = L * l;
    const perim = 2 * (L + l);
    const somme = L + l;
    const phrase = `Aire = L × l = ${fmt(L)} × ${fmt(l)} = {{${fmt(aire)}}} ${u2}.
`
        + `Périmètre = 2 × (L + l) = 2 × (${fmt(L)} + ${fmt(l)}) = 2 × {{${fmt(somme)}}} = {{${fmt(perim)}}} ${unite}.`;
    const texte = `<p>Calcule l'aire et le périmètre d'un rectangle de longueur ${fmt(L)} ${unite} et de largeur ${fmt(l)} ${unite}.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PERIM), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'aire_perimetre';
    return payload;
};

const genPerimetreRelier = () => {
    const unite = randomChoice(UNITES);
    const u2 = unite + '²';
    const figures = [];
    const seen = new Set();

    let attempts = 0;
    while (figures.length < 2 && attempts < 50) {
        const c = randCote();
        const aire = c * c;
        if (!seen.has(aire)) {
            seen.add(aire);
            figures.push({ source: `Carré de côté ${fmt(c)} ${unite}`, target: `${fmt(aire)} ${u2}` });
        }
        attempts++;
    }
    attempts = 0;
    while (figures.length < 4 && attempts < 50) {
        const { L, l } = randRect(false);
        const aire = L * l;
        if (!seen.has(aire)) {
            seen.add(aire);
            // « L = … et l = … » (revue 2026-07-31) : l'écriture « 8 cm × 3 cm »
            // pré-mâchait la multiplication — les lettres nomment les dimensions.
            figures.push({ source: `Rectangle avec L = ${fmt(L)} ${unite} et l = ${fmt(l)} ${unite}`, target: `${fmt(aire)} ${u2}` });
        }
        attempts++;
    }
    while (figures.length < 4) {
        const c = randInt(2, 12);
        figures.push({ source: `Carré de côté ${fmt(c)} ${unite}`, target: `${fmt(c * c)} ${u2}` });
    }

    const texte = `<p>Relie chaque figure à son aire.</p>`;
    const payload = makeRelier(figures, texte, opts(L_PERIM));
    payload._meta.generatorKey = 'aire_perimetre';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    calcul_aire_figure_qcm_unique:       genFigureQcm,
    calcul_aire_figure_texte:            genFigureTexte,
    aire_perimetre_qcm_unique:           genPerimetreQcm,
    aire_perimetre_texte_trou_saisie:    genPerimetreTrou,
    aire_perimetre_relier:               genPerimetreRelier,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcul_aire_figure', label: L_FIGURE, formatsList: buildFormats('calcul_aire_figure') },
    { id: 'aire_perimetre', label: L_PERIM, formatsList: buildFormats('aire_perimetre') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
